import { useImmer } from "use-immer";
import { useEffect, useState } from "react";
import UtilService from "../../service/UtilService";
import Note from "../Note";
import CardLockKillSwitchGeneralConfig from "../dashboardPage/CardLockKillSwitchGeneralConfig";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashCan } from "@fortawesome/free-solid-svg-icons/faTrashCan";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons/faTriangleExclamation";
import { faCircleQuestion } from "@fortawesome/free-regular-svg-icons";

const Component = ({ config, onChange }) => {
  const panSuppressTemplate = { pan: "", suppressEndTime: "", isNew: true };
  const [data, updateData] = useImmer(config);
  const [killSwitchInfoOpen, setKillSwitchInfoOpen] = useState(false);
  const [panSuppressInfoOpen, setPanSuppressInfoOpen] = useState(false);
  const [cardLockKillSwitchInfoOpen, setCardLockKillSwitchInfoOpen] = useState(false);

  const handleInfoIconClick = (detailsId, isOpen, setOpen) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(!isOpen);
  };

  useEffect(() => {
    updateData(config);
  }, [config]);

  useEffect(() => {
    onChange(data);
  }, [data]);

  const addPanSuppress = () => {
    panSuppressTemplate.suppressEndTime =
      UtilService.mapHoursRemainingToIsoOffsetDateTimeString(24);
    updateData((d) => {
      d.generalConfig.panSuppressConfig.panSuppresses.unshift(
        panSuppressTemplate,
      );
    });
  };

  return (
    data && (
      <>
        <h3>
          Kill switch - PABLO{" "}
          <span className="section-header-icons">
            <Note
              noteTargetPath="generalConfig.killSwitch"
              noteTargetId={data.id}
            />
            <FontAwesomeIcon
              icon={faCircleQuestion}
              className="info-icon"
              onClick={handleInfoIconClick(
                "killSwitch-details",
                killSwitchInfoOpen,
                setKillSwitchInfoOpen,
              )}
              title={killSwitchInfoOpen ? "Hide info" : "Show info"}
            />
          </span>
        </h3>

        {killSwitchInfoOpen && (
          <div className="info-box">
            <p>
              <strong>Forces all transactions to be approved</strong> as a
              failsafe to prevent service disruption caused by configuration
              errors or bugs. It can be triggered manually or automatically if
              the system's decline rate exceeds a safety threshold, ensuring
              legitimate customers are not blocked during system issues.
            </p>
          </div>
        )}

        <table className="config-table">
          <thead>
            <tr>
              <th></th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr
              className={
                data.generalConfig.killSwitchConfig.isActive ? "warn" : ""
              }
            >
              <td>Status</td>
              <td>
                <input
                  type="checkbox"
                  checked={data.generalConfig.killSwitchConfig.isActive}
                  onChange={(e) =>
                    updateData((d) => {
                      d.generalConfig.killSwitchConfig.isActive =
                        !d.generalConfig.killSwitchConfig.isActive;
                    })
                  }
                />
                &nbsp;
                {data.generalConfig.killSwitchConfig.isActive ? (
                  <>
                    Kill switch is active{" "}
                    <FontAwesomeIcon
                      icon={faTriangleExclamation}
                      className="fa-fade"
                    />
                  </>
                ) : (
                  "Inactive"
                )}
              </td>
            </tr>
            <tr>
              <td>
                Auto-on interval <small>[hours]</small>{" "}
              </td>
              <td>
                <input
                  type="number"
                  value={
                    data.generalConfig.killSwitchConfig
                      .autoTurnOnTimeIntervalHours
                  }
                  onChange={(e) =>
                    updateData((d) => {
                      d.generalConfig.killSwitchConfig.autoTurnOnTimeIntervalHours =
                        e.target.value;
                    })
                  }
                />
              </td>
            </tr>
            <tr>
              <td>
                Auto-on declined <small>[%]</small>
              </td>
              <td>
                <input
                  type="number"
                  step={0.01}
                  value={
                    data.generalConfig.killSwitchConfig
                      .autoTurnOnDeclinedTransactionThresholdRatio * 100
                  }
                  onChange={(e) =>
                    updateData((d) => {
                      let val = e.target.value / 100;
                      val = val > 0.008 ? 0.008 : val;
                      val = val < 0 ? 0 : val;
                      d.generalConfig.killSwitchConfig.autoTurnOnDeclinedTransactionThresholdRatio =
                        val;
                    })
                  }
                />
              </td>
            </tr>
          </tbody>
        </table>

        <h3>
          Pan suppress{" "}
          <span className="section-header-icons">
            <Note
              noteTargetPath="generalConfig.panSuppress"
              noteTargetId={data.id}
            />
            <FontAwesomeIcon
              icon={faCircleQuestion}
              className="info-icon"
              onClick={handleInfoIconClick(
                "panSuppress-details",
                panSuppressInfoOpen,
                setPanSuppressInfoOpen,
              )}
              title={panSuppressInfoOpen ? "Hide info" : "Show info"}
            />
          </span>
        </h3>

        {panSuppressInfoOpen && (
          <div className="info-box">
            <p>
              <strong>Automatically approves transactions</strong> for specific PANs on a
              suppression list, bypassing the standard risk evaluation.
              Suppressions are temporary and ensure that flagged cards are
              always approved by Pablo during that period.
            </p>
          </div>
        )}

        <table className="config-table table-with-button">
          <thead>
            <tr>
              <th>Pan</th>
              <th>
                Remaining <small>[hours]</small>
              </th>
              <th>
                <FontAwesomeIcon
                  icon={faPlus}
                  className="icon-clickable"
                  onClick={() => addPanSuppress()}
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {data &&
              data.generalConfig.panSuppressConfig.panSuppresses.map(
                (ps, index) => (
                  <tr className="" key={index}>
                    <td>
                      <input
                        type="text"
                        value={ps.pan}
                        disabled={!ps.isNew}
                        onChange={(e) =>
                          updateData((d) => {
                            d.generalConfig.panSuppressConfig.panSuppresses[
                              index
                            ].pan = e.target.value;
                          })
                        }
                      />
                    </td>

                    <td>
                      <input
                        type="number"
                        value={UtilService.mapIsoOffsetDateTimeStringToHoursRemaining(
                          ps.suppressEndTime,
                        )}
                        onChange={(e) =>
                          updateData((d) => {
                            d.generalConfig.panSuppressConfig.panSuppresses[
                              index
                            ].suppressEndTime =
                              UtilService.mapHoursRemainingToIsoOffsetDateTimeString(
                                e.target.value,
                              );
                          })
                        }
                      />
                    </td>
                    <td>
                      <FontAwesomeIcon
                        icon={faTrashCan}
                        className="icon-clickable"
                        onClick={(e) =>
                          updateData((d) => {
                            d.generalConfig.panSuppressConfig.panSuppresses.splice(
                              index,
                              1,
                            );
                          })
                        }
                      />
                    </td>
                  </tr>
                ),
              )}
          </tbody>
        </table>

        <h3>
          Kill switch - WAG Card Lock{" "}
          <span className="section-header-icons">
            <Note
              noteTargetPath="generalConfig.cardLockKillSwitch"
              noteTargetId={data.id}
            />
            <FontAwesomeIcon
              icon={faCircleQuestion}
              className="info-icon"
              onClick={handleInfoIconClick(
                "cardLockKillSwitch-details",
                cardLockKillSwitchInfoOpen,
                setCardLockKillSwitchInfoOpen,
              )}
              title={cardLockKillSwitchInfoOpen ? "Hide info" : "Show info"}
            />
          </span>
        </h3>

        {cardLockKillSwitchInfoOpen && (
          <div className="info-box">
            <p>
              <strong>Temporarily disables Card Lock checks</strong> if a high rate of
              transactions are rejected due to "Card locked" errors (often
              caused by system outages preventing users from unlocking their
              cards). When triggered, it suppresses the lock for a set time
              (default 60 minutes) to allow legitimate transactions,
              re-evaluating the error rate once the period ends.
            </p>
          </div>
        )}

        <CardLockKillSwitchGeneralConfig
          config={data.generalConfig.cardLockKillSwitchConfig}
          onChange={(e) =>
            updateData((d) => {
              d.generalConfig.cardLockKillSwitchConfig = e;
            })
          }
        ></CardLockKillSwitchGeneralConfig>
      </>
    )
  );
};

export default Component;
